
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface ResearchDesignSectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const ResearchDesignSection = ({ onComplete, initialData }: ResearchDesignSectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'researchLeadership',
      title: 'Research Leadership',
      question: 'How do you approach product research?',
      options: [
        { text: 'I actively participate in research activities and apply findings to inform product decisions. I value research insights in my work.', points: 2 },
        { text: 'I design effective research studies that answer specific questions. I select appropriate research methods for different situations.', points: 4 },
        { text: 'I drive research initiatives that shape product strategy across multiple features or products. I translate research findings into actionable product direction.', points: 7 },
        { text: 'I develop innovative research methodologies that others adopt. My approach to research influences how the organization learns about users and markets.', points: 10 }
      ],
      evidence: [
        { 
          title: 'Research Initiative',
          description: 'Describe a significant research initiative you led'
        },
        {
          title: 'Method Selection',
          description: 'Why did you choose this research approach?'
        }
      ]
    },
    {
      id: 'designExcellence',
      title: 'Design Excellence',
      question: 'How do you ensure excellent product design?',
      options: [
        { text: 'I partner effectively with designers and respect their expertise. I can articulate what makes good design for my product area.', points: 2 },
        { text: 'I contribute meaningfully to design strategy and represent the user perspective. I help set design direction aligned with business goals.', points: 4 },
        { text: 'I influence key design decisions that balance user needs, technical constraints, and business objectives. I help resolve complex design challenges.', points: 7 },
        { text: 'I establish design frameworks that guide product development across multiple features or products. My design thinking shapes the user experience vision.', points: 10 }
      ],
      evidence: [
        {
          title: 'Design Contribution',
          description: 'Share an example of your contribution to product design'
        },
        {
          title: 'Design Impact',
          description: 'How did your design direction improve the product outcome?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Research & Design Mastery assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default ResearchDesignSection;
